import { ChevronDown, Comments, ClipboardList, FileArchive } from '@assets/svgs';
import { Button } from '@components/button';
import { Grid, Typography, Divider, Theme, Box } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Dispatch, useMemo } from 'react';
import {
  TPolicyDetailFunctionBody,
  getBrokerPolicyDetailMenuItems,
  getPolicyDetailsMenuItems,
} from '@pages/PolicyDetails/PolicyDetails.constants';
import { Menu, IMenuItemExtended } from '@components/menu';
import {
  useGetOoseResolution,
  useGetPolicy,
  useSearchPolicyTransactions,
  useSearchReviewAtRenewal,
} from '@services/policy';
import { useSearchPolicyTerms } from '@services/policyTerm/useSearchPolicyTerms';
import invariant from 'tiny-invariant';
import Loading from '@components/Loading';
import { AllPolicyDialogTypes, PolicyDialogType } from '@models/policyTransaction';

import { getCurrentTermCount, getFormattedPolicyNumber } from '@utils/policy';
import { IconButton } from '@components/iconButton';
import { usePolicyMetadata } from '@services/policy/usePolicyMetadata';
import { Badge } from '@components/badge/Badge';
import { useUserViewedPolicyMessages } from '@services/policy/useUserViewedPolicyMessages';
import { getIsFeatureFlagEnabled, FEATURE_FLAG } from '@utils/featureFlags';
import { Tooltip } from '@components/tooltip';
import { useCreateDocumentGeneration } from '@services/document';
import { PackageTypeId } from '@models/document';

export const PolicyDetailsHeader = ({
  anchorEl,
  setAnchorEl,
  handleMenuClick,
  setDialog,
  billingMethod,
}: {
  anchorEl: null | HTMLElement;
  setAnchorEl: Dispatch<HTMLElement | null>;
  handleMenuClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  setDialog: React.Dispatch<React.SetStateAction<{ type: AllPolicyDialogTypes | undefined; show: boolean }>>;
  billingMethod: string;
}) => {
  const { policyId } = useParams();
  invariant(policyId);
  const [userAuth, currentUser] = useStore((state) => [state.userAuth, state.currentUser], shallow);
  const { userViewedPolicyMessages } = useUserViewedPolicyMessages();

  const { policyMetadata } = usePolicyMetadata({ policyId });
  const { policyTerms } = useSearchPolicyTerms({
    queryParams: { pageLimit: 100 },
    policyId: policyId ?? '',
  });
  const { policy } = useGetPolicy({ policyId });
  const { policyTransactions } = useSearchPolicyTransactions({
    policyId: policyId ?? '',
    includeCanceled: false,
    pageLimit: 100,
  });

  const { reviewAtRenewalItems } = useSearchReviewAtRenewal({
    policyId: policyId ?? '',
  });

  const { ooseResolution } = useGetOoseResolution({ policyId });
  const hasOoseResolutionInProgress = ooseResolution?.status === 'InProgress';

  const { mutate: createDocumentGeneration } = useCreateDocumentGeneration();

  const handleCreateLossRunReport = () => {
    createDocumentGeneration({
      packageTypeId: PackageTypeId.LossHistoryReport,
      policyId: policyId,
    });
  };
  const actionItems: IMenuItemExtended<TPolicyDetailFunctionBody>[] = useMemo(() => {
    const baseItems = userAuth.isInternalUser
      ? getPolicyDetailsMenuItems({ handleCreateLossRunReport })
      : userAuth.isBroker
        ? getBrokerPolicyDetailMenuItems({ handleCreateLossRunReport })
        : [];

    return baseItems.map((item) => {
      const policyInfo: TPolicyDetailFunctionBody = {
        packageLabel: policy?.packageLabel,
        status: policy?.status,
        doNotRenew: Boolean(policy?.doNotRenew),
        renewalStatus: policy?.renewalStatus,
        policyTransactions: policyTransactions?.items ?? [],
        relatedPolicySources: policy?.relatedPolicySources,
        relatedPolicyDestinations: policy?.relatedPolicyDestinations,
        userAuth,
        policyTerms: policyTerms?.items ?? [],
        billingMethod,
        offeringId: policy?.boundQuote?.offering?.id,
        hasActiveReviewAtRenewal: (policy?.reviewsAtRenewal?.length ?? 0) > 0,
        hasReviewAtRenewalActivity: (reviewAtRenewalItems?.items?.length ?? 0) > 0,
        hasPendingBORTransferRequest: !!policy?.pendingBORTransferRequest,
      };

      return {
        ...item,
        action: item.showDialogType
          ? () => handleOpenDialog(item.showDialogType as AllPolicyDialogTypes)
          : item.action
            ? item.action
            : undefined,
        hidden: item.isShown && policy?.status ? !item.isShown(policyInfo) : true,
        disabled: item.isDisabled && policy?.status ? item.isDisabled(policyInfo) : userAuth?.isLioSales,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy, userAuth, policyTerms?.items, policyTransactions?.items, reviewAtRenewalItems?.items, billingMethod]);

  const isActionButtonDisabled = useMemo(() => {
    if (hasOoseResolutionInProgress) return true;
    return !!policy?.rewrittenPolicy && !(userAuth.isAdmin || userAuth.isPortalAdmin);
  }, [policy, userAuth, hasOoseResolutionInProgress]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (type: AllPolicyDialogTypes) => {
    setAnchorEl(null);
    setDialog((prev: { type: AllPolicyDialogTypes | undefined; show: boolean }) => ({
      type: type || prev.type || undefined,
      show: true,
    }));
  };

  const handleOnClickMenuItem = (index: number) => {
    if (actionItems[index]?.action !== undefined) {
      actionItems[index].action?.();
      setAnchorEl(null);
    }
  };

  const policyNumber = policy
    ? `${getFormattedPolicyNumber(
        policy.policyNumberPrefix,
        policy?.policyNumber.policyNumber,
        getCurrentTermCount(policyTerms?.items),
      )}`
    : undefined;

  const isPolicyChatEnabled = getIsFeatureFlagEnabled(FEATURE_FLAG.POLICY_CHAT, {
    isInternalUser: userAuth.isInternalUser,
  });

  return !policy ? (
    <Loading />
  ) : (
    <>
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          pb: '24px',
          display: 'flex',
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Grid
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              pr: '50px',
            }}
          >
            <Typography
              variant='caption'
              css
              sx={{
                color: 'text.subdued',
                lineHeight: '16px',
                display: 'block',
                paddingBottom: '8px',
              }}
            >
              Policy Number
            </Typography>
            <Typography variant='h4' sx={{ lineHeight: '20px' }}>
              {policyNumber}
            </Typography>
          </Grid>
          <Grid
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {policy.rewriteOfPolicy ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant='bodySmall'>Rewrite of Policy: </Typography>
                <Link to={`/policy/${policy.rewriteOfPolicy?.id}`} style={{ textDecoration: 'none' }}>
                  {/* TODO- replace with LIO link component, it shoudl be green already */}
                  <Typography
                    variant='bodySmall'
                    sx={{
                      fontWeight: 'bold',
                      color: 'secondary.main',

                      ':hover': {
                        borderBottom: 'borders.green',
                      },
                    }}
                  >
                    {policy.rewriteOfPolicy &&
                      getFormattedPolicyNumber(
                        policy.rewriteOfPolicy.policyNumberPrefix,
                        policy.rewriteOfPolicy.policyNumber.policyNumber,
                      )}
                  </Typography>
                </Link>
              </Box>
            ) : null}
            {policy.rewriteOfPolicy && policy.rewrittenPolicy ? (
              <Box sx={{ margin: '0 16px', alignContent: 'center' }}>
                <Box
                  sx={{ height: '20px', borderRight: `1px solid ${({ palette }: Theme) => palette.text.subdued}` }}
                />
              </Box>
            ) : null}
            {policy.rewrittenPolicy ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant='bodySmall'>Rewritten as Policy: </Typography>
                <Link to={`/policy/${policy.rewrittenPolicy?.id}`} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant='bodySmall'
                    sx={{
                      fontWeight: 'bold',
                      color: 'secondary.main',

                      ':hover': {
                        borderBottom: 'borders.green',
                      },
                    }}
                  >
                    {policy.rewrittenPolicy &&
                      getFormattedPolicyNumber(
                        policy.rewrittenPolicy.policyNumberPrefix,
                        policy.rewrittenPolicy.policyNumber.policyNumber,
                      )}
                  </Typography>
                </Link>
              </Box>
            ) : null}
          </Grid>
        </Box>
        <Box sx={{ display: { sm: 'flex' } }}>
          <Box sx={{ display: 'flex' }}>
            {userAuth.isInternalUser && (
              <Tooltip enterDelay={1000} title='View or add notes related to this policy' placement='top'>
                <IconButton
                  sx={{
                    mr: 1,
                  }}
                  onClick={() => {
                    handleOpenDialog(PolicyDialogType.Notes);
                  }}
                >
                  <ClipboardList width='20px' height='20px' />
                </IconButton>
              </Tooltip>
            )}
            {userAuth.isInternalUser && (
              <Tooltip enterDelay={1000} title='View document archive for this policy' placement='top'>
                <IconButton
                  sx={{
                    mr: 1,
                  }}
                  onClick={() => {
                    handleOpenDialog(PolicyDialogType.DocumentArchive);
                  }}
                >
                  <FileArchive width='20px' height='20px' />
                </IconButton>
              </Tooltip>
            )}
            {isPolicyChatEnabled && !userAuth.isCustomerUser && (
              <Tooltip enterDelay={1000} title='View or add messages related to this policy' placement='top'>
                <IconButton
                  sx={{
                    mr: 1,
                  }}
                  onClick={() => {
                    handleOpenDialog(PolicyDialogType.Messages);
                    if (currentUser) {
                      userViewedPolicyMessages({ policyId: policy.id, userId: currentUser.id });
                    }
                  }}
                >
                  <Badge
                    badgeContent={policyMetadata?.currentUser?.unreadMessageNotificationCount ?? 0}
                    maxCount={9}
                    sx={{
                      '& .MuiBadge-badge': {
                        height: '16px',
                        width: '14px',
                        padding: 1,
                        right: 0,
                        color: 'common.white',
                        backgroundColor: 'error.main',
                      },
                    }}
                  >
                    <Comments width='24px' height='24px' />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {actionItems?.length > 0 && (userAuth.isInternalUser || userAuth.isBroker) && (
            <Tooltip
              title={
                hasOoseResolutionInProgress
                  ? 'No new transactions can be created while an Out of Sequence Endorsement resolution is in progress.'
                  : ''
              }
              placement='top'
            >
              <span>
                <Button
                  onClick={handleMenuClick}
                  disableRipple
                  variant='outlined'
                  disabled={isActionButtonDisabled}
                  endIcon={<ChevronDown width='12px' />}
                  sx={{
                    alignSelf: 'center',
                    padding: '8px 16px',
                    marginRight: '0px',
                    '.MuiButton-endIcon': {
                      marginTop: '2px',
                    },
                  }}
                >
                  Actions
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        {actionItems?.length > 0 && (
          <Menu
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleMenuClose}
            onClickMenuItem={handleOnClickMenuItem}
            items={actionItems}
          />
        )}
      </Grid>
      <Divider style={{ marginBottom: '24px', color: 'divider' }} />
    </>
  );
};
